import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BUSINESS_TYPES_OPTIONS } from 'vopay';

/**
 * Localized labels for the account-creation "Business Type" dropdown.
 *
 * `BUSINESS_TYPES_OPTIONS` is a `vopay` constant whose **keys** ('Sole Trader', 'Corporation', …) are
 * the values submitted to the API and matched on in logic, and whose values are the English display
 * labels. Per the i18n-conventions "config-/API-derived labels that double as logic keys" rule, the
 * keys are kept verbatim and only the rendered string is localized — so the submitted payload is
 * byte-identical to before.
 *
 * The result is derived from the constant rather than hand-listed, so the option set and its order
 * still come from one place: an entry added to the constant appears here automatically, rendering its
 * English label until a translation is supplied. Nothing ever renders blank.
 *
 * That fallback is also the blind spot — an entry this hook has no mapping for renders in English and
 * looks identical to a deliberate pass-through. Neither committed i18n guard can see it (there is no
 * `t()` call site for the missing key and no hardcoded literal to find), so the only way to catch one
 * is to open the account-creation form in French and read the dropdown.
 *
 * Lives here rather than in the component library because partner-dashboard is the constant's only
 * consumer — localizing at the source would buy nothing and cost a submodule PR.
 */
export default function useBusinessTypeOptions(): { [businessTypeCode: string]: string } {
  const { t } = useTranslation('translations', { keyPrefix: 'Hooks.UseBusinessTypeOptions' });

  // `t`'s identity changes when the language does, so the labels rebuild on a language switch and
  // hold a stable identity otherwise — callers can safely put the result in a dependency array.
  return useMemo(() => {
    // Keyed by the raw constant key, NOT by the English label — the key is the stable code. A Map
    // rather than an object literal because the codes contain spaces, which the object-key naming
    // lint rule rejects.
    const labels = new Map<string, string>([
      ['Sole Trader', t('soleTrader', { defaultValue: 'Individual or sole proprietorship' })],
      ['Corporation', t('corporation', { defaultValue: 'Corporation' })],
      ['Registered Charity', t('registeredCharity', { defaultValue: 'Associations, Charities and Not for Profits' })],
      ['Incorporated Partnership', t('incorporatedPartnership', { defaultValue: 'Partnership' })],
      ['Public Limited Company', t('publicLimitedCompany', { defaultValue: 'Public Limited Company' })],
      ['Other', t('other', { defaultValue: 'Other' })],
    ]);

    return Object.fromEntries(
      Object.entries(BUSINESS_TYPES_OPTIONS).map(([businessTypeCode, englishLabel]) => [
        businessTypeCode,
        labels.get(businessTypeCode) ?? englishLabel,
      ]),
    );
  }, [t]);
}
